import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  message,
  Popconfirm,
  Tag,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DragOutlined
} from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../utils/auth';

const { TextArea } = Input;
const { Option } = Select;

const sectionTypeMap = {
  baggage: { label: '行李规定', color: 'blue' },
  customs: { label: '海关申报', color: 'orange' },
  insurance: { label: '旅游保险', color: 'green' },
  payment: { label: '支付方式', color: 'purple' },
  other: { label: '其他', color: 'default' }
};

const NoticeTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form] = Form.useForm();

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/notice-templates`, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (error) {
      message.error('获取模板列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, sortOrder: 0 });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingTemplate(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handlePreview = (record) => {
    setPreviewContent(record);
    setPreviewVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE}/notice-templates/${id}`, {
        headers: AuthService.getAuthHeaders()
      });
      message.success('删除成功');
      fetchTemplates();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingTemplate) {
        await axios.put(`${API_BASE}/notice-templates/${editingTemplate.id}`, values, {
          headers: AuthService.getAuthHeaders()
        });
        message.success('更新成功');
      } else {
        await axios.post(`${API_BASE}/notice-templates`, values, {
          headers: AuthService.getAuthHeaders()
        });
        message.success('创建成功');
      }

      setModalVisible(false);
      fetchTemplates();
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      message.error('操作失败');
    }
  };

  const columns = [
    {
      title: '排序',
      dataIndex: 'sortOrder',
      width: 60
    },
    {
      title: '板块类型',
      dataIndex: 'sectionType',
      width: 100,
      render: (type) => (
        <Tag color={sectionTypeMap[type]?.color}>
          {sectionTypeMap[type]?.label || type}
        </Tag>
      )
    },
    {
      title: '标题',
      dataIndex: 'title',
      width: 140
    },
    {
      title: '标题(英文)',
      dataIndex: 'titleEn',
      width: 140,
      render: (text) => text || <span style={{ color: '#999' }}>-</span>
    },
    {
      title: '内容预览',
      dataIndex: 'content',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text?.substring(0, 200)}>
          {text?.substring(0, 50)}...
        </Tooltip>
      )
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      width: 70,
      render: (active) => (
        <Tag color={active ? 'green' : 'default'}>
          {active ? '启用' : '禁用'}
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 110,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="预览">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handlePreview(record)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除此模板吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="通用模板设置"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            新建模板
          </Button>
        }
      >
        <p style={{ color: '#666', marginBottom: 16 }}>
          通用模板用于出团通知书中的固定内容，如行李规定、海关申报说明、旅游保险说明等。
          这些内容会应用到所有旅游产品的通知书中。
        </p>
        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 800 }}
          size="small"
        />
      </Card>

      {/* 编辑/新建模态框 */}
      <Modal
        title={editingTemplate ? '编辑模板' : '新建模板'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={800}
        okText={editingTemplate ? '保存' : '创建'}
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="sectionType"
            label="板块类型"
            rules={[{ required: true, message: '请选择板块类型' }]}
          >
            <Select placeholder="选择板块类型">
              {Object.entries(sectionTypeMap).map(([key, value]) => (
                <Option key={key} value={key}>
                  {value.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="title"
            label="标题 (中文)"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="如：行李规定" />
          </Form.Item>

          <Form.Item name="titleEn" label="标题 (英文)">
            <Input placeholder="如：Baggage Allowance" />
          </Form.Item>

          <Form.Item
            name="content"
            label="内容 (中文)"
            rules={[{ required: true, message: '请输入内容' }]}
          >
            <TextArea
              rows={8}
              placeholder="输入模板内容，支持换行..."
            />
          </Form.Item>

          <Form.Item name="contentEn" label="内容 (英文)">
            <TextArea rows={6} placeholder="输入英文内容..." />
          </Form.Item>

          <Space>
            <Form.Item name="sortOrder" label="排序">
              <InputNumber min={0} placeholder="0" />
            </Form.Item>

            <Form.Item name="isActive" label="启用状态" valuePropName="checked">
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      {/* 预览模态框 */}
      <Modal
        title={`预览：${previewContent?.title}`}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={700}
      >
        {previewContent && (
          <div>
            <h3>
              <Tag color={sectionTypeMap[previewContent.sectionType]?.color}>
                {sectionTypeMap[previewContent.sectionType]?.label}
              </Tag>
              {previewContent.title}
              {previewContent.titleEn && (
                <span style={{ color: '#999', fontSize: 14, marginLeft: 8 }}>
                  ({previewContent.titleEn})
                </span>
              )}
            </h3>
            <div
              style={{
                background: '#f5f5f5',
                padding: 16,
                borderRadius: 8,
                whiteSpace: 'pre-wrap',
                marginTop: 16
              }}
            >
              {previewContent.content}
            </div>
            {previewContent.contentEn && (
              <div
                style={{
                  background: '#e6f7ff',
                  padding: 16,
                  borderRadius: 8,
                  whiteSpace: 'pre-wrap',
                  marginTop: 16
                }}
              >
                <strong>English:</strong>
                <br />
                {previewContent.contentEn}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default NoticeTemplates;
